import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH23_QUESTIONS } from "../data/neetPhysicsCh23Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 23: Electromagnetic Waves (NCERT Class 12, Ch. 8).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example / structured reference table. NOT YET
// human-reviewed by a subject-matter expert — treat as a first draft and
// verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Displacement Current & Maxwell's Equations", "Properties of EM Waves", "The Electromagnetic Spectrum", "Applications of EM Spectrum Bands"];

const SECTION_COLORS = {
  "Displacement Current & Maxwell's Equations": "#2563eb",
  "Properties of EM Waves":                     "#7c3aed",
  "The Electromagnetic Spectrum":               "#dc2626",
  "Applications of EM Spectrum Bands":          "#16a34a",
};

const CONCEPTS = [
  // ── Displacement Current & Maxwell's Equations ─────────────────────────────
  { section: "Displacement Current & Maxwell's Equations", title: "Displacement current — why Ampere's law needed it",
    explanation: "Ampere's circuital law, ∮B.dl = μ0.I, relates the magnetic field around a closed loop to the conduction current through ANY surface bounded by that loop. But for a charging capacitor, a flat surface passing between the plates carries NO conduction current, while a different (bulged) surface crossing the connecting wire carries the full current I -- giving two different answers for the same loop. Maxwell resolved this by proposing that a changing ELECTRIC FLUX also produces a magnetic field, exactly as a conduction current does. This equivalent 'current', called displacement current, is Id = ε0 (dΦE/dt) -- not an actual flow of charge, but present wherever an electric field is changing with time, even in vacuum.",
    example: `Charging capacitor, loop drawn around the connecting wire:
Flat surface (cuts wire) encloses current I; curved surface
(bulges into the gap) encloses NO conduction current -> contradiction

Id = ε0 (dΦE/dt)     (ΦE = electric flux through the surface)

For a parallel-plate capacitor, area A, field E = Q/(ε0.A):
Id = ε0 . d/dt(E.A) = ε0.A . dE/dt = dQ/dt = I
  -> Id in the gap exactly equals the conduction current I in the wires` },
  { section: "Displacement Current & Maxwell's Equations", title: "Ampere-Maxwell law — the modified Ampere's circuital law",
    explanation: "Adding displacement current as a second source term gives the Ampere-Maxwell law: ∮B.dl = μ0.I + μ0.ε0(dΦE/dt). This restores consistency -- for ANY surface bounded by the loop, the sum of conduction current and displacement current through it comes out the same, resolving the earlier contradiction and correctly predicting the magnetic field even inside a charging capacitor's gap.",
    example: `∮ B.dl = μ0 Ic + μ0 ε0 (dΦE/dt)
              |________________|
              total effective ("Maxwell") current

Flat surface: Ic = I,  Id = 0        -> total = I
Curved surface: Ic = 0, Id = I       -> total = I   (consistent now!)` },
  { section: "Displacement Current & Maxwell's Equations", title: "Maxwell's four equations and the prediction of EM waves",
    explanation: "Maxwell unified electricity and magnetism into four equations. (1) Gauss's law (electricity): field lines originate/terminate on charges. (2) Gauss's law (magnetism): no magnetic monopoles exist -- field lines always form closed loops. (3) Faraday's law: a changing magnetic flux induces an electric field. (4) Ampere-Maxwell law: a current OR a changing electric flux produces a magnetic field. Combining (3) and (4) shows a changing E creates a changing B and vice versa -- these can sustain each other indefinitely with no nearby charges, propagating outward as a self-supporting electromagnetic wave. This purely theoretical prediction was later confirmed experimentally by Heinrich Hertz.",
    example: `1) Gauss's law (E):     ∮E.dA = q / ε0
2) Gauss's law (B):     ∮B.dA = 0                    (no monopoles)
3) Faraday's law:       ∮E.dl = -dΦB/dt              (changing B -> E)
4) Ampere-Maxwell law:  ∮B.dl = μ0 I + μ0ε0(dΦE/dt)   (I or changing E -> B)

Changing E --(4)--> changing B --(3)--> changing E ... (self-sustaining)
Predicted speed in vacuum: v = 1/sqrt(mu0.eps0) ~ 3x10^8 m/s = speed of light` },

  // ── Properties of EM Waves ──────────────────────────────────────────────────
  { section: "Properties of EM Waves", title: "Transverse nature — E, B mutually perpendicular and perpendicular to propagation",
    explanation: "In an electromagnetic wave, both the electric field E and magnetic field B oscillate PERPENDICULAR to the direction of propagation (making EM waves transverse, unlike longitudinal sound waves) -- and they are also perpendicular to EACH OTHER. If the wave travels along x with E along y, then B must point along z, such that E x B points along the direction of propagation. This right-handed triad (E, B, direction of travel) holds at every point and instant, with E and B oscillating in phase at the same frequency.",
    example: `Wave travelling along +x, E along +y, B along +z:
Ey = E0 sin(kx - wt),   Bz = B0 sin(kx - wt)   (same phase, same w)

Check: E(hat) x B(hat) = y(hat) x z(hat) = x(hat)  <- matches propagation
Neither E nor B has a component along x -> confirms transverse nature` },
  { section: "Properties of EM Waves", title: "Speed of EM waves in vacuum: c = 1/√(μ0ε0)",
    explanation: "From his equations, Maxwell derived that electromagnetic waves travel through vacuum at a fixed speed determined entirely by two electromagnetic constants: the permeability of free space μ0 and the permittivity of free space ε0. This speed, c = 1/sqrt(μ0.ε0), came out numerically equal to the already-measured speed of light -- strong evidence that light itself is an electromagnetic wave.",
    example: `c = 1 / sqrt(mu0 . eps0)

mu0  = 4*pi x 10^-7  T.m/A
eps0 = 8.85 x 10^-12  C^2/(N.m^2)

c = 1 / sqrt[(4*pi x 10^-7)(8.85 x 10^-12)] ~ 3.00 x 10^8 m/s
  (matches the measured speed of light -> light is an EM wave)` },
  { section: "Properties of EM Waves", title: "Relation between amplitudes: E0/B0 = c",
    explanation: "At every point and instant in an EM wave, the ratio of the electric field's magnitude to the magnetic field's magnitude equals the speed of light: E/B = c (equivalently for the amplitudes, E0/B0 = c). This means the electric field is always numerically much LARGER than the magnetic field in SI units (V/m vs tesla), even though both carry comparable energy on average.",
    example: `E0 / B0 = c = 3 x 10^8 m/s

If B0 = 2 x 10^-8 T, then:
E0 = B0 . c = (2 x 10^-8)(3 x 10^8) = 6 V/m

(a fixed ratio, not a statement that E and B are "equal" -- their
numerical values differ hugely because of the units involved)` },
  { section: "Properties of EM Waves", title: "Energy, momentum, and medium-free propagation of EM waves",
    explanation: "An electromagnetic wave carries both electric and magnetic energy (equal on time-average) and, because this energy flows in a definite direction, the wave also carries momentum -- so it exerts RADIATION PRESSURE on any surface it strikes. Because an EM wave is a self-sustaining interplay of changing E and B fields, it needs NO material medium to travel through, unlike mechanical waves such as sound -- this is why light and radio signals cross the vacuum of space.",
    example: `Average energy density: u_avg = eps0.E0^2   (electric + magnetic, equal shares)
Radiation pressure: P = Intensity/c (full absorption); 2(Intensity)/c (full reflection)

Sound (mechanical): needs a medium; cannot cross vacuum
EM wave: self-sustaining E & B fields; crosses vacuum at c = 3x10^8 m/s` },

  // ── The Electromagnetic Spectrum ────────────────────────────────────────────
  { section: "The Electromagnetic Spectrum", title: "Overview: the full electromagnetic spectrum",
    explanation: "The electromagnetic spectrum is the complete range of EM waves, ordered by wavelength/frequency: radio waves, microwaves, infrared, visible light, ultraviolet, X-rays, and gamma rays -- from longest wavelength/lowest frequency to shortest wavelength/highest frequency (and hence lowest to highest photon energy). All of these travel at the same speed c in vacuum; only wavelength, frequency, and energy vary across the spectrum.",
    example: `Increasing frequency, decreasing wavelength  ------------------->
Radio | Microwave | Infrared | Visible | Ultraviolet | X-ray | Gamma

All travel at c = 3x10^8 m/s in vacuum;  c = (frequency) x (wavelength)
Higher frequency  <->  shorter wavelength  <->  higher photon energy` },
  { section: "The Electromagnetic Spectrum", title: "Radio waves — production and wavelength range",
    explanation: "Radio waves are produced by accelerating charges in a conductor, typically an oscillating electric current fed to an antenna via an LC (tank) circuit. They span an enormous wavelength range, roughly from about 0.1 m up to several hundred metres (frequencies from a few hertz up to a few hundred megahertz), and are the longest-wavelength, lowest-energy band of the spectrum.",
    example: `Production: oscillating current in an LC circuit -> radiated by
             an antenna

Typical range: ~0.1 m  to  several hundred m
(AM radio ~ 300 m - 550 m;  FM radio ~ 3 m;  varies by application)` },
  { section: "The Electromagnetic Spectrum", title: "Microwaves — production and wavelength range",
    explanation: "Microwaves are produced by special vacuum-tube devices such as klystrons and magnetrons, which generate oscillating currents at gigahertz frequencies. Their wavelength range is roughly 1 mm to 0.1 m. Because their wavelength is short enough to be focused into narrow beams, they are well-suited to radar and to depositing energy efficiently into water molecules.",
    example: `Production: klystron valve, magnetron

Typical range: ~1 mm to 0.1 m  (frequencies ~ 1 GHz to a few hundred GHz)

Short wavelength -> can be formed into narrow, directional beams
                    (used in radar antennas / satellite dishes)` },
  { section: "The Electromagnetic Spectrum", title: "Infrared radiation — production and wavelength range",
    explanation: "Infrared (IR) radiation is produced by hot bodies and by molecules undergoing vibrational and rotational motion -- essentially any object above absolute zero emits some IR, which is why it is often called 'heat radiation'. Its wavelength range is roughly 700 nm to 1 mm, lying between visible light and microwaves.",
    example: `Production: hot objects; molecular vibration & rotation
            (all warm bodies, including the human body, emit IR)

Typical range: ~700 nm to 1 mm

Absorbed strongly by greenhouse gases -> important in the
Earth's atmospheric greenhouse effect` },
  { section: "The Electromagnetic Spectrum", title: "Visible light — the narrow band we can see",
    explanation: "Visible light is the only part of the electromagnetic spectrum directly detectable by the human eye, spanning roughly 700 nm (red) down to about 400 nm (violet). It is produced by atomic electron transitions (e.g. in the Sun, incandescent filaments, LEDs) and, despite being a tiny sliver of the full spectrum, dominates human perception of the world.",
    example: `Visible range: ~700 nm (red) down to ~400 nm (violet)

Roughly, in order of increasing frequency (decreasing wavelength):
Red - Orange - Yellow - Green - Blue - Indigo - Violet  (VIBGYOR)

Production: electron transitions between atomic/molecular energy levels` },
  { section: "The Electromagnetic Spectrum", title: "Ultraviolet radiation — production and wavelength range",
    explanation: "Ultraviolet (UV) radiation is produced by very hot bodies (the Sun is a major natural source) and by special UV lamps/arcs. Its wavelength range is roughly 400 nm down to about 1 nm (some texts extend it down to ~0.6 nm), placing it between visible light and X-rays. Excess UV exposure can damage living tissue and DNA.",
    example: `Production: the Sun, UV lamps, electric arcs

Typical range: ~400 nm down to ~1 nm (up to ~0.6 nm in some references)

Sub-bands: UV-A (~400-315 nm), UV-B (~315-280 nm), UV-C (~280-100 nm)
  -- UV-C is the most damaging to living cells` },
  { section: "The Electromagnetic Spectrum", title: "X-rays — production and wavelength range",
    explanation: "X-rays are produced when high-speed electrons are suddenly decelerated (\"braked\") upon striking a heavy metal target, as in an X-ray tube; they can also arise from certain inner-shell electron transitions in heavy atoms. Their wavelength range is roughly 1 nm down to about 10^-3 nm, giving them high energy and strong penetrating power through soft tissue.",
    example: `Production: high-energy electrons suddenly decelerated at a
            metal target (X-ray tube); inner-shell atomic transitions

Typical range: ~1 nm down to ~10^-3 nm

High penetrating power through soft tissue, absorbed more by
denser material (bone) -> basis of diagnostic radiography` },
  { section: "The Electromagnetic Spectrum", title: "Gamma rays — production and wavelength range",
    explanation: "Gamma rays are produced by radioactive decay of atomic nuclei and by other nuclear reactions/processes (e.g. nuclear fission, fusion, particle-antiparticle annihilation). They occupy the shortest-wavelength, highest-frequency, and highest-energy end of the electromagnetic spectrum -- wavelengths below about 10^-3 nm, overlapping the short-wavelength end of the X-ray band.",
    example: `Production: radioactive nuclear decay, nuclear reactions

Typical range: less than ~10^-3 nm  (shortest wavelength, highest
                energy band of the EM spectrum)

Highly penetrating and ionizing -> requires thick shielding
(e.g. lead, concrete) for safe handling` },

  // ── Applications of EM Spectrum Bands ───────────────────────────────────────
  { section: "Applications of EM Spectrum Bands", title: "Radio waves & microwaves — communication, ionosphere, radar and ovens",
    explanation: "Radio waves are the workhorse of wireless communication -- radio and TV broadcasting, mobile networks, and point-to-point links -- since they are easily generated and radiated by antennas. Long-distance ('over the horizon') communication is possible because certain radio frequencies are REFLECTED back toward Earth by the ionosphere (an ionized upper-atmosphere layer), letting signals bend around the Earth's curvature. Microwaves, being shorter wavelength, can be focused into narrow beams -- ideal for RADAR (navigation, speed detection) -- and are used in microwave ovens, whose frequency is tuned to be strongly absorbed by water molecules in food, heating it from within.",
    example: `RADIO WAVES                    | MICROWAVES
--------------------------------|--------------------------------
Uses: AM/FM radio, TV, mobile   | Uses: radar, satellite links,
  communication                 |   microwave ovens
Key effect: ionosphere reflects | Key effect: short wavelength ->
  suitable frequencies back to  |   narrow directional beams;
  Earth -> long-distance/       |   resonates with water
  "skywave" communication       |   molecules -> efficient heating` },
  { section: "Applications of EM Spectrum Bands", title: "Infrared & ultraviolet — remote controls/thermal imaging and sterilization/ozone layer",
    explanation: "Infrared is used in household remote controls (via small IR LEDs) and in thermal/night-vision cameras, which detect the IR naturally emitted by warm bodies -- it is also central to the atmospheric greenhouse effect. Ultraviolet's germicidal effect (damaging microorganism DNA) makes it useful for sterilizing water, surgical instruments, and surfaces, and it is essential for vitamin D synthesis in skin -- but excessive exposure causes skin cancer and cataracts. Fortunately, most of the Sun's harmful UV is absorbed by the OZONE LAYER in the upper atmosphere; its depletion (the 'ozone hole') lets more damaging UV reach the surface.",
    example: `INFRARED                       | ULTRAVIOLET
--------------------------------|--------------------------------
Uses: TV/AC remote controls,    | Uses: sterilizing water/
  thermal imaging, night vision |   instruments, vitamin D
Key effect: greenhouse gases    | Key effect: OZONE LAYER absorbs
  trap re-radiated IR, warming  |   most harmful solar UV;
  the atmosphere                |   depletion -> more UV reaches
                                 |   the ground -> skin cancer risk` },
  { section: "Applications of EM Spectrum Bands", title: "X-rays & gamma rays — medical imaging and cancer treatment",
    explanation: "X-rays are best known for diagnostic radiography: since they penetrate soft tissue easily but are absorbed more by dense bone, they reveal fractures and (via CT scans) detailed body cross-sections; industrially they detect flaws in metal castings/welds. Gamma rays, of even higher energy, are used in radiotherapy to destroy cancerous cells and shrink tumours (e.g. a 'Gamma Knife' or cobalt-60 unit), as radioactive tracers in diagnosis, for sterilizing medical equipment, and industrially to detect flaws in thick metal parts.",
    example: `X-RAYS                         | GAMMA RAYS
--------------------------------|--------------------------------
Uses: diagnostic radiography    | Uses: cancer radiotherapy,
  (fractures, dental, CT scans),|   sterilizing equipment,
  airport baggage scanning,     |   radioactive tracers,
  industrial flaw detection     |   industrial flaw detection
Caution: ionizing -> lead       | Caution: highest energy/most
  aprons/shielding used          |   penetrating -> heavy shielding` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh23Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh23Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh23Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH23_QUESTIONS : NEET_PHYSICS_CH23_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 23</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Electromagnetic Waves</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 8 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH23_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH23_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
